/**
 * EmailOrdersDialog — invio riepilogo ordini via email.
 * Più destinatari separati da "," o ";" + note aggiuntive incluse nel testo.
 */
import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Loader2, Mail } from 'lucide-react';
import { toast } from 'sonner';

const LS_KEY = 'orders_email_recipients';

export default function EmailOrdersDialog({ open, onClose, orderIds, onSent }) {
  const [recipients, setRecipients] = useState('');
  const [notes, setNotes] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open) {
      setNotes('');
      setRecipients(localStorage.getItem(LS_KEY) || '');
    }
  }, [open]);

  const parsed = recipients.split(/[,;]/).map(s => s.trim()).filter(Boolean);

  const send = async () => {
    if (parsed.length === 0) { toast.error('Inserisci almeno un destinatario'); return; }
    setSending(true);
    try {
      const r = await api.post('/orders/email-summary', {
        order_ids: orderIds, recipients: parsed, notes: notes.trim() || null,
      });
      localStorage.setItem(LS_KEY, recipients);
      toast.success(`Riepilogo inviato a ${r.data.sent_to.join(', ')}`);
      onSent();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Invio email fallito');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="email-orders-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Mail className="w-5 h-5" /> Invia riepilogo via email</DialogTitle>
          <DialogDescription>
            {orderIds.length} {orderIds.length === 1 ? 'ordine selezionato' : 'ordini selezionati'} — il riepilogo arriva formattato e diviso per negozio, pronto da integrare.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600 block mb-1">
              Destinatari * <span className="normal-case font-normal text-slate-400">(separati da &quot;,&quot; o &quot;;&quot;)</span>
            </label>
            <Input value={recipients} onChange={e => setRecipients(e.target.value)}
                   placeholder="ordini@fornitore.it; mario@esempio.it"
                   className="rounded-none" data-testid="email-recipients-input" autoFocus />
            {parsed.length > 0 && (
              <p className="text-[11px] text-slate-500 mt-1" data-testid="email-recipients-count">
                {parsed.length} destinatari: {parsed.join(' · ')}
              </p>
            )}
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-600 block mb-1">Note aggiuntive (incluse nella mail)</label>
            <Textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} maxLength={2000}
                      placeholder="Es. consegna urgente entro venerdì…"
                      className="rounded-none resize-none" data-testid="email-notes-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={send} disabled={sending || parsed.length === 0} className="gap-1.5" data-testid="email-send-btn">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Invia
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
